/**
 * Utility functions for handling difficulty levels consistently across the application.
 * Database stores difficulty_level as INTEGER (1-5), these functions map to display labels and colors.
 */

export const getDifficultyLabel = (level: number): string => {
  switch (level) {
    case 1:
      return 'beginner'
    case 2:
      return 'easy'
    case 3:
      return 'intermediate'
    case 4:
      return 'hard'
    case 5:
      return 'advanced'
    default:
      return 'unknown'
  }
}

export const getDifficultyColor = (level: number): string => {
  switch (level) {
    case 1:
      return 'bg-green-100 text-green-800'
    case 2:
      return 'bg-green-200 text-green-900'
    case 3:
      return 'bg-yellow-100 text-yellow-800'
    case 4:
      return 'bg-orange-100 text-orange-800'
    case 5:
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

/**
 * Translation function type for i18next compatibility
 */
type TranslationFunction = {
  (key: string): string;
  (key: string, defaultValue: string): string;
  (key: string, options?: Record<string, string>): string;
};

/**
 * Get localized difficulty label with translation fallback
 */
export const getLocalizedDifficultyLabel = (level: number, t: TranslationFunction): string => {
  const label = getDifficultyLabel(level)
  return t(`common.${label}`, label)
}

/**
 * Validate if a difficulty level is within valid range (1-5)
 */
export const isValidDifficultyLevel = (level: number): boolean => {
  return Number.isInteger(level) && level >= 1 && level <= 5
}

/**
 * Convert legacy string difficulty to number (for migration purposes)
 */
export const convertLegacyDifficulty = (difficulty: string | number): number => {
  if (typeof difficulty === 'number') {
    return isValidDifficultyLevel(difficulty) ? difficulty : 1
  }

  switch (difficulty.toLowerCase()) {
    case 'beginner':
    case 'easy':
      return 1
    case 'intermediate':
    case 'medium':
      return 3
    case 'advanced':
    case 'hard':
      return 5
    default:
      return 1
  }
}